import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, FileText, Plus, ArrowLeft, Sparkles, Calendar, Loader, Pencil, X, Download, Check, Trash2, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import VoiceRecorder from '@/components/VoiceRecorder';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const NotesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editDate, setEditDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMember, setFilterMember] = useState('');
  const [formData, setFormData] = useState({
    member_id: '',
    content: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [aiPrompt, setAiPrompt] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    fetchNotes();
    fetchMembers();
  }, [token]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes`, { headers });
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`, { headers });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notes`, formData, { headers });
      setShowForm(false);
      setFormData({ member_id: '', content: '', date: new Date().toISOString().split('T')[0] });
      setAiPrompt('');
      fetchNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditContent(note.content);
    setEditDate(note.date);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
    setEditDate('');
  };

  const saveEdit = async (noteId) => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/notes/${noteId}`, { content: editContent, date: editDate }, { headers });
      setEditingId(null);
      fetchNotes();
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingEdit(false);
    }
  };

  const downloadPdf = async (noteId) => {
    try {
      const response = await axios.get(`${API}/notes/${noteId}/pdf`, {
        headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `note-${noteId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('PDF download failed:', error);
      window.open(`${API}/notes/${noteId}/pdf?token=${token}`, '_blank');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/notes/${noteId}`, { headers });
      fetchNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAiAssist = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const selectedMember = members.find(m => m.id === formData.member_id);
      const memberContext = selectedMember
        ? `${selectedMember.first_name} ${selectedMember.last_name}, né(e) le ${selectedMember.date_of_birth}${selectedMember.diagnosis ? ', diagnostic: ' + selectedMember.diagnosis : ''}`
        : null;
      const response = await axios.post(`${API}/notes/ai-assist`, { prompt: aiPrompt, member_context: memberContext }, { headers });
      setFormData({ ...formData, content: response.data.generated_text });
    } catch (error) {
      console.error('AI assist failed:', error);
      alert('Erreur lors de la génération IA.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVoiceReport = (data) => {
    setFormData({ ...formData, content: data.report, member_id: data.member_id || formData.member_id });
    setAiPrompt(`Transcription : ${data.transcription}`);
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? `${member.first_name} ${member.last_name}` : 'Bénéficiaire';
  };

  const filteredNotes = notes.filter(n => {
    if (filterMember && n.member_id !== filterMember) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (n.content || '').toLowerCase().includes(q) ||
      (n.date || '').includes(q) ||
      getMemberName(n.member_id).toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading">
              Notes de service
            </h1>
          </div>
          <Button
            data-testid="add-note-btn"
            onClick={() => setShowForm(!showForm)}
            className="w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showForm ? 'Annuler' : 'Nouvelle note'}
          </Button>
        </div>

        {!showForm && notes.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="note-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par contenu, date..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
              />
            </div>
            <select
              data-testid="note-filter-member"
              value={filterMember}
              onChange={(e) => setFilterMember(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all sm:w-56"
            >
              <option value="">Tous les bénéficiaires</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
              ))}
            </select>
          </div>
        )}

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg">Nouvelle note de service</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Bénéficiaire</label>
                      <select
                        data-testid="note-member-select"
                        name="member_id"
                        value={formData.member_id}
                        onChange={handleChange}
                        className="flex w-full rounded-xl border-slate-200 bg-white px-4 py-3 text-base transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                        required
                      >
                        <option value="">Sélectionnez un bénéficiaire</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                      <Input data-testid="note-date-input" type="date" name="date" value={formData.date} onChange={handleChange} required />
                    </div>
                  </div>

                  <VoiceRecorder token={token} memberId={formData.member_id} onReportGenerated={handleVoiceReport} />

                  <div className="bg-secondary-50 rounded-2xl p-4 border-2 border-secondary-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-secondary" />
                      <h3 className="font-bold text-secondary-900 text-sm md:text-base">Assistant IA Claude</h3>
                    </div>
                    <div className="space-y-3">
                      <Textarea
                        data-testid="ai-prompt-input"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="Décrivez ce qui s'est passé aujourd'hui..."
                        className="min-h-[80px]"
                      />
                      <Button data-testid="ai-generate-btn" type="button" onClick={handleAiAssist} variant="secondary" disabled={aiLoading || !aiPrompt.trim()}>
                        {aiLoading ? <><Loader className="w-5 h-5 mr-2 animate-spin" />Génération...</> : <><Sparkles className="w-5 h-5 mr-2" />Générer avec l'IA</>}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Contenu de la note</label>
                    <Textarea data-testid="note-content-input" name="content" value={formData.content} onChange={handleChange} placeholder="Rédigez votre note ici..." className="min-h-[150px] md:min-h-[200px]" required />
                  </div>

                  <Button data-testid="submit-note-btn" type="submit" className="w-full">Enregistrer la note</Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {notes.length === 0 ? (
          <Card>
            <CardContent className="p-8 md:p-12 text-center">
              <FileText className="w-12 h-12 md:w-16 md:h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">Aucune note</h3>
              <p className="text-sm md:text-base text-slate-600 mb-6">Commencez par rédiger votre première note de service</p>
              <Button data-testid="empty-add-note-btn" onClick={() => setShowForm(true)}>
                <Plus className="w-5 h-5 mr-2" />Nouvelle note
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredNotes.length === 0 && (searchQuery || filterMember) ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucune note ne correspond à votre recherche</p>
                </CardContent>
              </Card>
            ) : null}
            {filteredNotes.map((note) => (
              <motion.div key={note.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card data-testid={`note-card-${note.id}`}>
                  <CardContent className="p-4 md:p-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 md:p-3 bg-secondary-50 rounded-full flex-shrink-0">
                          <FileText className="w-5 h-5 md:w-6 md:h-6 text-secondary" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base md:text-lg font-bold text-primary-900 mb-1 truncate">
                            {getMemberName(note.member_id)}
                          </h3>
                          <div className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
                            <Calendar className="w-3.5 h-3.5" />
                            {editingId === note.id ? (
                              <Input
                                type="date"
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                                className="h-7 text-xs w-36"
                              />
                            ) : (
                              note.date
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {note.ai_generated && (
                          <span className="hidden sm:flex items-center gap-1 px-2 py-1 bg-secondary-50 text-secondary rounded-full text-xs font-medium">
                            <Sparkles className="w-3.5 h-3.5" />IA
                          </span>
                        )}
                        {editingId === note.id ? (
                          <>
                            <Button data-testid={`save-note-${note.id}`} size="sm" onClick={() => saveEdit(note.id)} disabled={savingEdit} className="text-xs">
                              {savingEdit ? <Loader className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Sauvegarder</>}
                            </Button>
                            <Button data-testid={`cancel-edit-${note.id}`} size="sm" variant="ghost" onClick={cancelEdit} className="text-xs">
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <button
                              data-testid={`edit-note-${note.id}`}
                              onClick={() => startEdit(note)}
                              className="p-2 text-slate-400 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`download-note-${note.id}`}
                              onClick={() => downloadPdf(note.id)}
                              className="p-2 text-slate-400 hover:text-secondary hover:bg-secondary-50 rounded-lg transition-colors"
                              title="Télécharger PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`delete-note-${note.id}`}
                              onClick={() => deleteNote(note.id)}
                              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    {editingId === note.id ? (
                      <Textarea
                        data-testid={`edit-content-${note.id}`}
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="min-h-[150px] md:min-h-[200px]"
                      />
                    ) : (
                      <p className="text-sm md:text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {note.content}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPage;
